import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import useSorting from 'src/registry/hooks/useSorting';
import { useListWebhookEndpoints } from 'src/features/webhook-endpoints/api/useListWebhookEndpoints';
import { WebhookEndpoint } from 'src/features/webhook-endpoints/utils/types';
import { webhookEndpointsMessages } from 'src/features/webhook-endpoints/utils/messages';
import { generalMessages } from 'src/utils/messages';
import CreateWebhookEndpointFormModal from 'src/features/webhook-endpoints/components/forms/CreateWebhookEndpointFormModal';
import CellWebhookEndpointActions from 'src/features/webhook-endpoints/components/tables/CellWebhookEndpointActions';
import { getTooltipCell } from 'src/utils/getTooltipCell';

const WebhookEndpointsTable = () => {
    const { formatMessage } = useIntl();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: false }],
    });

    const {
        data: webhookEndpoints,
        isLoading,
        isError,
    } = useListWebhookEndpoints({
        ...querySearchOptions,
        ...queryPaginationOptions,
        ...querySortingOptions,
    });

    const columnHelper = createColumnHelper<WebhookEndpoint>();

    const columns = [
        columnHelper.accessor('url', {
            enableSorting: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(webhookEndpointsMessages.url),
            },
        }),
        columnHelper.accessor('topics', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return row.original.topics?.map((topic) => topic).join(', ');
            },
            meta: {
                title: formatMessage(webhookEndpointsMessages.topics),
            },
        }),
        columnHelper.accessor('secret', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(webhookEndpointsMessages.secret),
            },
        }),
        columnHelper.accessor('is_active', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(voucherTypesMessages.isActive),
            },
        }),
        columnHelper.accessor('description', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => getTooltipCell({ value: row.original.description ? row.original.description : '-' }),
            meta: {
                title: formatMessage(generalMessages.description),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellWebhookEndpointActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={webhookEndpoints}
            semantics={{
                title: formatMessage(webhookEndpointsMessages.webhookEndpoints),
                entryName: formatMessage(webhookEndpointsMessages.webhook).toLowerCase(),
                entryNames: formatMessage(webhookEndpointsMessages.webhooks).toLowerCase(),
                placeholder: formatMessage(webhookEndpointsMessages.webhook).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry: CreateWebhookEndpointFormModal,
            }}
        />
    );
};

export default WebhookEndpointsTable;
